import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'next-i18next'
import { graphqlAPI } from '../utils/graphql'
import './PaymentProcessing.scss'

const PaymentProcessing = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()
  
  const [orderData, setOrderData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [paymentAmount, setPaymentAmount] = useState(null)
  // Redirection flow does not require Redsys script
  const initiatedRef = useRef(false)

  useEffect(() => {
    // Restore order context for summary
    const storedOrder = sessionStorage.getItem('ticketOrder')
    if (!storedOrder) {
      navigate('/')
      return
    }

    const order = JSON.parse(storedOrder)
    if (!order.orderId) {
      navigate('/cart')
      return
    }
    setOrderData(order)

    if (!order.email) {
      setError(t('payment.noContactInfo'))
      setLoading(false)
      return
    }

    // If Redsys returned with URLKO to this route, parse query params
    const searchParams = new URLSearchParams(location.search)
    const mp = searchParams.get('Ds_MerchantParameters')
    const sv = searchParams.get('Ds_SignatureVersion')
    const sig = searchParams.get('Ds_Signature')

    if (mp || sv || sig) {
      // We have a gateway result; decode and show error/success
      try {
        const decoded = mp ? JSON.parse(atob(mp)) : {}
        const responseCode = decoded?.Ds_Response || 'UNKNOWN'
        const description = decoded?.Ds_Response_Description
        // Redsys success is code '0000'
        if (responseCode === '0000') {
          setError('')
          setLoading(false)
        } else {
          const message = description
            ? `${t('payment.gatewayError', { code: responseCode })} – ${description}`
            : t('payment.gatewayError', { code: responseCode })
          setError(message)
          setLoading(false)
        }
      } catch (e) {
        // If decoding fails, show a generic error but allow retry
        setError(t('errors.unknown'))
        setLoading(false)
      }
      // Do not auto-initiate payment when returning from Redsys
      return
    }

    // Guard against double invocation in dev (React StrictMode)
    if (!initiatedRef.current) {
      initiatedRef.current = true
      initiatePayment(order)
    }
  }, [navigate, location.search])

  const initiatePayment = async (order) => {
    try {
      setLoading(true)
      setError('')
      // Generate Redsys payment data (merchant parameters and signature)
      const paymentData = await graphqlAPI.generatePayment(order.orderId, order.quantity, order.total)
      if (!paymentData?.redsysUrl || !paymentData?.merchantParameters || !paymentData?.signature) {
        throw new Error('Redsys payload incompleto: falta URL, parámetros o firma')
      }
      // Decode merchant parameters to extract amount (in cents)
      try {
        const decoded = JSON.parse(atob(paymentData.merchantParameters))
        const cents = Number(decoded?.DS_MERCHANT_AMOUNT || decoded?.Ds_Merchant_Amount || decoded?.Ds_MerchantParameters?.DS_MERCHANT_AMOUNT || 0)
        if (!isNaN(cents) && cents > 0) {
          setPaymentAmount((cents / 100).toFixed(2))
        }
      } catch (_) {
        // Ignore decode errors; summary will fall back to original order total
      }
      // Redirection flow: create and submit the Redsys form in a new tab
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = paymentData.redsysUrl
      // Submit in the same tab to avoid popup blockers
      form.target = '_self'
      const params = {
        'Ds_SignatureVersion': 'HMAC_SHA256_V1',
        'Ds_MerchantParameters': paymentData.merchantParameters,
        'Ds_Signature': paymentData.signature,
      }
      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        form.appendChild(input)
      })
      document.body.appendChild(form)
      form.submit()
      setLoading(false)
      
    } catch (err) {
      const serverMessage = err?.response?.errors?.[0]?.message || err?.message || ''
      const msgLower = serverMessage.toLowerCase()
      if (msgLower.includes('no contact information available for payment')) {
        setError(t('payment.noContactInfo'))
      } else if (msgLower.includes('order is already fully paid')) {
        setError(t('payment.orderFullyPaid'))
      } else if (serverMessage) {
        setError(serverMessage)
      } else {
        setError(t('errors.unknown'))
      }
      setLoading(false)
    }
  }

  const handleRetry = () => {
    if (orderData) {
      initiatePayment(orderData)
    }
  }

  const handleBackToCart = () => {
    navigate('/cart')
  }

  if (!orderData) {
    return (
      <div className="payment-processing">
        <div className="container">
          <div className="text-center">
            <div className="spinner"></div>
            <p className="mt-3">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="payment-processing">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="payment-card card">
              <div className="card-body text-center">
                {loading ? (
                  <div className="processing-state">
                    <div className="payment-icon">
                      <i className="fas fa-credit-card"></i>
                    </div>
                    <h3>{t('payment.processing')}</h3>
                    <p className="text-muted mb-4">
                      {t('payment.redirecting')}
                    </p>
                    <div className="spinner-large mb-4"></div>
                    <p className="small text-muted">
                      {t('payment.pleaseWait')}
                    </p>
                  </div>
                ) : error ? (
                  <div className="error-state">
                    <div className="error-icon">
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <h3>{t('payment.error')}</h3>
                    <p className="text-muted mb-4">
                      {error}
                    </p>
                    <div className="error-actions">
                      <Link to="/cart"
                        className="back-link left me-3"
                        onClick={handleBackToCart}
                      >
                        {t('payment.backToCart')}
                      </Link>
                       <button
                        className="btn btn-primary me-3"
                        onClick={handleRetry}
                      >
                        <i className="fas fa-redo me-2"></i>
                        {t('payment.retry')}
                      </button>
                     
                    </div>
                  </div>
                ) : null}

                {/* No iframe for redirection flow */}

                {/* Order Summary */}
                <div className="order-summary mt-4">
                  <h5>{t('payment.orderSummary')}</h5>
                  <div className="summary-details">
                    <p><strong>{orderData.eventName}</strong></p>
                    <p>
                      {orderData.quantity} × {orderData.pricePerTicket}€ = {orderData.total}€
                    </p>
                    {paymentAmount && (
                      <p><strong>{t('payment.totalToPay')}:</strong> {paymentAmount}€</p>
                    )}
                    <p className="text-muted">{orderData.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentProcessing
